// src/pages/YatraListing.js
import React, { useState, useEffect, useRef } from 'react';
import Header from './common/Header';
import Footer from './common/Footer';
import LoginPopup from './popup/LoginPopup';
import ReviewModal from './popup/ReviewModal';
import SEO from '../components/common/SEO';
import './YatraListing.css';
import './SearchSuggestions.css';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
// eslint-disable-next-line no-unused-vars
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getValidSession } from './helpers/sessionUtils';
// eslint-disable-next-line no-unused-vars
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { buildVendorProfileUrl } from '../utils/vendorProfileUrl';
import ListingVerifiedBadge from '../components/common/ListingVerifiedBadge';
import VendorProfileButton from '../components/common/VendorProfileButton';
import ListingFilterBar from '../components/common/ListingFilterBar';
import { formatPrice } from '../utils/priceUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];
const vendorProfileUrl = (item) => buildVendorProfileUrl(item?.Vendor);

// --- STAR RATING COMPONENT ---
const StarRating = ({ avg = 0, count = 0, onClick }) => {
    const fullStars = Math.floor(avg);
    const hasHalf = avg - fullStars >= 0.5;

    return (
        <div className="d-flex align-items-center justify-content-center gap-2" style={{ cursor: 'pointer' }} onClick={onClick}>
            <div className="d-flex" style={{ gap: "2px" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <i
                        key={star}
                        className={`bi ${star <= fullStars ? "bi-star-fill" : star === fullStars + 1 && hasHalf ? "bi-star-half" : "bi-star"} text-warning`}
                        style={{ fontSize: "16px" }}
                    />
                ))}
            </div>
            <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                {Math.round(avg)}/5
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
                ({count} reviews)
            </span>
        </div>
    );
};

export const generateYatraSlug = (yatra) => {
    const slugify = (str) =>
        str
            ? str
                .toString()
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^\w-]+/g, '')
                .replace(/--+/g, '-')
                .replace(/^-+/, '')
                .replace(/-+$/, '')
            : '';
    const parts = [
        slugify(yatra.yatra_name || yatra.name),
        slugify(yatra.city),
        Math.round(yatra.offer_price || yatra.yatra_price || yatra.price || 0),
        yatra.id
    ].filter(Boolean).join('-');
    return parts.replace(/--+/g, '-');
};

// --- YATRA CARD COMPONENT ---
const YatraCard = ({ yatra, onAction, onReviewClick, onCardClick, isSubmitting, pendingAction, activeId }) => {
    const isActive = activeId === yatra.id && isSubmitting;
    const displayImage = yatra.image || '/no_image_teerth-yatra.jpg';

    // Calculate average rating
    const avgRating = yatra.avg_rating || 0;
    const reviewCount = yatra.review_count || 0;

    return (
        <div
            className="card mb-2 shadow-sm rounded-2 bg-white overflow-hidden result-card-hover"
            style={{
                cursor: 'default',
                border: '1px solid #e0e0e0'
            }}
        >
            <div className="row g-0 align-items-stretch">
                {/* IMAGE */}
                <div className="col-12 col-md-4 col-lg-3 p-0 d-flex align-items-stretch justify-content-stretch bg-light-subtle">
                    <div
                        className="rounded-start overflow-hidden shadow-sm w-100 position-relative"
                        style={{
                            borderColor: '#ddd'
                        }}
                    >
                        <img
                            src={displayImage}
                            alt={yatra.yatra_name}
                            className="w-100 h-100"
                            style={{ objectFit: "cover", display: "block", cursor: "pointer" }}
                            onClick={() => onCardClick(yatra)}
                            onError={(e) => {
                                e.target.src = '/no_image_teerth-yatra.jpg';
                            }}
                        />
                        <ListingVerifiedBadge item={yatra} />
                        {/* DURATION BADGE - TOP RIGHT */}
                        <div
                            className="position-absolute badge bg-primary"
                            style={{
                                top: '12px',
                                right: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px'
                            }}
                        >
                            {yatra.nights ? `${yatra.nights}N/${yatra.days}D` : (yatra.days ? `${yatra.days}D` : '')}
                        </div>
                    </div>
                </div>

                {/* INFO */}
                <div className="col-12 col-md-8 col-lg-6 p-3 text-start">
                    <div>
                        {/* Title */}
                        <h4
                            style={{ color: '#333', fontSize: '18px', cursor: 'pointer' }}
                            onClick={() => onCardClick(yatra)}
                        >
                            {yatra.yatra_name}
                        </h4>

                        {/* Location & Duration Tags */}
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                            <span className="badge bg-light text-dark" style={{ fontSize: '12px' }}>
                                <i className="bi bi-geo-alt-fill me-1" style={{ color: '#dc3545' }}></i> {yatra.city}
                            </span>

                            <span className="badge bg-light text-dark" style={{ fontSize: '12px' }}>
                                <i className="bi bi-calendar-event me-1" style={{ color: '#0dcaf0' }}></i> {yatra.nights ? `${yatra.nights}N/${yatra.days}D` : (yatra.days ? `${yatra.days} Days` : 'Duration N/A')}
                            </span>
                            {vendorProfileUrl(yatra) && (
                                <VendorProfileButton to={vendorProfileUrl(yatra)} />
                            )}
                        </div>

                        {/* Description */}
                        <p className="mb-3 yatra-card-description" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                            {yatra.description?.length > 120 ? `${yatra.description.substring(0, 120)}...` : yatra.description}
                        </p>

                        {/* Starting From Price */}
                        <div className="mb-0">
                            <p className="mb-1 text-muted" style={{ fontSize: '12px' }}>Starting from</p>
                            <div className="d-flex align-items-baseline gap-2 flex-wrap">
                                {yatra.offer_price && yatra.yatra_price && Number(yatra.yatra_price) > Number(yatra.offer_price) ? (
                                    <>
                                        <del className="text-muted" style={{ fontSize: '14px', textDecoration: 'line-through' }}>
                                            ₹{formatPrice(Math.round(yatra.yatra_price))}
                                        </del>
                                        <h5 className="fw-bold mb-0" style={{ color: '#0095D3', fontSize: '18px' }}>
                                            ₹{formatPrice(Math.round(yatra.offer_price))}
                                        </h5>
                                    </>
                                ) : (
                                    <h5 className="fw-bold mb-0" style={{ color: '#0095D3', fontSize: '18px' }}>
                                        {yatra.offer_price ? `₹${formatPrice(Math.round(yatra.offer_price))}` : (yatra.yatra_price ? `₹${formatPrice(Math.round(yatra.yatra_price))}` : 'Price on request')}
                                    </h5>
                                )}
                                {(yatra.offer_price || yatra.yatra_price) && (
                                    <span style={{ fontSize: '12px', fontWeight: '500', color: '#666' }}>/per person</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* BUTTONS */}
                <div className="col-12 col-lg-3 p-3 p-lg-4 d-flex flex-column gap-2 border-start yatra-card-actions">
                    <div className="d-none d-lg-block mb-lg-3 pb-lg-3 border-bottom">
                        <StarRating avg={avgRating} count={reviewCount} onClick={() => onReviewClick(yatra)} />
                    </div>

                    <div className="d-none d-md-block d-lg-none mb-2 pb-2 border-bottom text-center">
                        <StarRating avg={avgRating} count={reviewCount} onClick={() => onReviewClick(yatra)} />
                    </div>

                    {/* Mobile: review summary left, call/WhatsApp icons right */}
                    <div className="yatra-mobile-action-row d-md-none">
                        <div className="yatra-mobile-rating">
                            <StarRating avg={avgRating} count={reviewCount} onClick={() => onReviewClick(yatra)} />
                        </div>
                        <div className="yatra-mobile-icons">
                            <button
                                className="yatra-mobile-icon-btn"
                                onClick={(e) => onAction(e, "call", yatra)}
                                disabled={isSubmitting}
                                title="Call Now"
                            >
                                {isActive && pendingAction === "call" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/call.png" alt="Call" />
                                )}
                            </button>
                            <button
                                className="yatra-mobile-icon-btn yatra-mobile-whatsapp-btn"
                                onClick={(e) => onAction(e, "whatsapp", yatra)}
                                disabled={isSubmitting}
                                title="WhatsApp"
                            >
                                {isActive && pendingAction === "whatsapp" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/whatsapp.png" alt="WhatsApp" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Desktop Action Buttons */}
                    <div className="d-none d-md-flex flex-column gap-2 w-100">
                        <button
                            className="btn btn-outline-primary fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, "call", yatra)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === "call" ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-telephone-fill me-2"></i>
                            )}
                            {isActive && pendingAction === "call" ? "Connecting..." : "Call Now"}
                        </button>

                        <button
                            className="btn btn-outline-success fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, "whatsapp", yatra)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === "whatsapp" ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-whatsapp me-2"></i>
                            )}
                            {isActive && pendingAction === "whatsapp" ? "Connecting..." : "WhatsApp Now"}
                        </button>

                        <button
                            className="btn btn-outline-dark fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100 listing-book-btn"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, "book", yatra)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === "book" ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : null}
                            {isActive && pendingAction === "book" ? "Processing..." : "Book Now"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- PAGINATION COMPONENT ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    return (
        <nav aria-label="Page navigation" className="my-4">
            <ul className="listing-pagination justify-content-center">
                <li className={`listing-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                        className="listing-page-link"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="bi bi-chevron-left"></i> Previous
                    </button>
                </li>

                {getPageNumbers().map((page, index) => (
                    <li
                        key={index}
                        className={`listing-page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                    >
                        <button
                            className={`listing-page-link ${page === currentPage ? 'active' : ''}`}
                            onClick={() => typeof page === 'number' && onPageChange(page)}
                            disabled={page === '...'}
                        >
                            {page}
                        </button>
                    </li>
                ))}

                <li className={`listing-page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                        className="listing-page-link"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next <i className="bi bi-chevron-right"></i>
                    </button>
                </li>
            </ul>
        </nav>
    );
};

function YatraListing() {
    const [yatras, setYatras] = useState([]);
    const [visibleYatras, setVisibleYatras] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [defaultYatras, setDefaultYatras] = useState([]);
    const [visibleDefaultYatras, setVisibleDefaultYatras] = useState([]);
    const [defaultPage, setDefaultPage] = useState(1);
    const yatrasPerPage = 20;
    const navigate = useNavigate();
    const location = useLocation();

    // Listing Quick Filters
    const [priceSort, setPriceSort] = useState('');
    const [isTopRated, setIsTopRated] = useState(false);
    const [isVerifiedOnly, setIsVerifiedOnly] = useState(false);

    // SEARCH & SUGGESTIONS STATE (mirrors TirthYatra page)
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const skipNextFetchRef = useRef(false);
    const searchContainerRef = useRef(null);

    // Filter state (kept for commented filters)
    const [selectedCity, setSelectedCity] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [durationMax, setDurationMax] = useState('Any');
    const [nameQuery, setNameQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [citySearchInput, setCitySearchInput] = useState('');

    // User & Lead state
    const [user, setUser] = useState(null);
    const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [activeEntity, setActiveEntity] = useState(null);
    const [showThanksModal, setShowThanksModal] = useState(false);

    // Review state
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedYatra, setSelectedYatra] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAP_KEY,
        libraries,
    });
    const fromAutoRef = useRef(null);

    // Click outside handler for search suggestions dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // eslint-disable-next-line no-unused-vars
    const getCityFromPlace = (place) => {
        if (!place) return '';
        const comp = place.address_components || [];
        const findType = (type) => {
            const c = comp.find((x) => x.types && x.types.includes(type));
            return c ? c.long_name : null;
        };
        return findType('locality') || findType('administrative_area_level_2') || place.name || '';
    };

    // Load user from session
    useEffect(() => {
        const session = getValidSession();
        if (session?.user?.user || session?.user) {
            setUser(session.user?.user || session.user);
        }
    }, []);

    // Suggestions autocomplete debounce effect (mirrors TirthYatra page)
    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        if (searchQuery.trim().length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const debounceTimer = setTimeout(() => {
            const fetchSuggestions = async () => {
                setIsSuggestionsLoading(true);
                try {
                    const response = await axios.get(`${API_BASE_URL}/yatras/search-yatras?q=${encodeURIComponent(searchQuery.trim())}`);
                    const yList = Array.isArray(response.data?.yatras) ? response.data.yatras : [];

                    // build city suggestions from returned yatras (unique cities)
                    const citySet = new Set();
                    const citySuggestions = [];
                    for (const y of yList) {
                        const city = (y.city || '').trim();
                        if (city && !citySet.has(city.toLowerCase())) {
                            citySet.add(city.toLowerCase());
                            citySuggestions.push({ type: 'city', id: null, name: city });
                        }
                    }

                    // yatra suggestions
                    const yatraSuggestions = yList.map(y => ({
                        type: 'yatra',
                        id: y.id,
                        name: y.yatra_name,
                        city: y.city,
                        price: y.offer_price ? y.offer_price : y.yatra_price,
                        yatra: y,
                    }));

                    const list = [...citySuggestions, ...yatraSuggestions];
                    setSuggestions(list);
                    if (list.length > 0) {
                        setShowSuggestions(true);
                    }
                } catch (error) {
                    console.error("Failed to fetch yatra suggestions:", error);
                    setSuggestions([]);
                } finally {
                    setIsSuggestionsLoading(false);
                }
            };
            fetchSuggestions();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    // Handle URL parameters sync & auto-search on route change
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cityParam = params.get('city') || '';
        const nameParam = params.get('name') || '';
        const qParam = params.get('q') || '';
        const activeSearch = cityParam || nameParam || qParam || '';

        skipNextFetchRef.current = true;
        setShowSuggestions(false);
        setSuggestions([]);
        setSearchQuery(activeSearch);
        setAppliedSearch(activeSearch);
        setSelectedCity(cityParam);
        setNameQuery(nameParam || qParam);
        setCitySearchInput(cityParam);

        if (cityParam) {
            fetchFilteredYatras({ city: cityParam, sort: sortBy, maxDays: durationMax, q: nameParam || qParam || '', searchKeyword: activeSearch });
        } else if (nameParam || qParam) {
            fetchFilteredYatras({ city: '', sort: sortBy, maxDays: durationMax, q: nameParam || qParam, searchKeyword: activeSearch });
        } else {
            fetchFilteredYatras({ city: '', sort: sortBy, maxDays: durationMax, q: '', searchKeyword: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const fetchFilteredYatras = async ({ city = '', sort = '', maxDays = 'Any', q = '', searchKeyword = '' } = {}) => {
        setIsLoading(true);
        try {
            const params = {};
            if (city) params.city = city;
            if (q) params.q = q;
            if (maxDays && maxDays !== 'Any') params.maxDays = maxDays;
            if (sort) params.sort = sort;

            let res = await axios.get(`${API_BASE_URL}/yatras/search-yatras-listing`, { params });
            let pkgs = Array.isArray(res.data?.yatras) ? res.data.yatras : [];

            // If city search returned 0 results, check if query matches a yatra name (q)
            if (pkgs.length === 0 && city && !q) {
                const fallbackRes = await axios.get(`${API_BASE_URL}/yatras/search-yatras-listing`, {
                    params: { q: city, maxDays: maxDays !== 'Any' ? maxDays : undefined, sort: sort || undefined }
                });
                if (Array.isArray(fallbackRes.data?.yatras) && fallbackRes.data.yatras.length > 0) {
                    pkgs = fallbackRes.data.yatras;
                }
            }

            setYatras(pkgs);
            setCurrentPage(1);

            const hasActiveSearch = Boolean((searchKeyword !== undefined ? searchKeyword : (city || q || appliedSearch || '')).trim());
            // If search returned 0 results and an active search was performed, fetch all default yatras
            if (pkgs.length === 0 && hasActiveSearch) {
                try {
                    const defRes = await axios.get(`${API_BASE_URL}/yatras/search-yatras-listing`);
                    const defPkgs = Array.isArray(defRes.data?.yatras) ? defRes.data.yatras : [];
                    setDefaultYatras(defPkgs);
                    setVisibleDefaultYatras(defPkgs.slice(0, yatrasPerPage));
                    setDefaultPage(1);
                } catch (defErr) {
                    console.error('Failed to fetch default yatras', defErr);
                    setDefaultYatras([]);
                    setVisibleDefaultYatras([]);
                }
            } else {
                setDefaultYatras([]);
                setVisibleDefaultYatras([]);
            }
        } catch (err) {
            console.error('Failed to fetch yatras', err);
            setYatras([]);
            setDefaultYatras([]);
            setVisibleDefaultYatras([]);
            setCurrentPage(1);
        } finally {
            setIsLoading(false);
        }
    };

    // Filter & Sort Yatras
    const filteredYatras = React.useMemo(() => {
        let list = [...yatras];

        // 1. Filter by Verified Vendors only (Aadhaar verified)
        if (isVerifiedOnly) {
            list = list.filter(item => {
                const vendor = item?.Vendor || item?.vendor;
                return vendor?.is_publicly_verified === true || Number(vendor?.is_publicly_verified) === 1;
            });
        }

        const getPrice = (item) => Number(item.offer_price) || Number(item.yatra_price) || 0;
        const getRating = (item) => Number(item.avg_rating || 0);
        const getReviewCount = (item) => Number(item.review_count || 0);

        // 2. Sort by Top Rated or Price
        if (isTopRated) {
            list.sort((a, b) => {
                const rDiff = getRating(b) - getRating(a);
                if (rDiff !== 0) return rDiff;
                const cDiff = getReviewCount(b) - getReviewCount(a);
                if (cDiff !== 0) return cDiff;
                if (priceSort === 'low_to_high') return getPrice(a) - getPrice(b);
                if (priceSort === 'high_to_low') return getPrice(b) - getPrice(a);
                return 0;
            });
        } else if (priceSort === 'low_to_high') {
            list.sort((a, b) => getPrice(a) - getPrice(b));
        } else if (priceSort === 'high_to_low') {
            list.sort((a, b) => getPrice(b) - getPrice(a));
        }

        return list;
    }, [yatras, isVerifiedOnly, isTopRated, priceSort]);

    // Reset page to 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [isVerifiedOnly, isTopRated, priceSort]);

    // Update visible yatras when filtered list or current page changes
    useEffect(() => {
        const startIndex = (currentPage - 1) * yatrasPerPage;
        const endIndex = startIndex + yatrasPerPage;
        setVisibleYatras(filteredYatras.slice(startIndex, endIndex));
    }, [filteredYatras, currentPage]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDefaultPageChange = (pageNumber) => {
        const startIndex = (pageNumber - 1) * yatrasPerPage;
        const endIndex = startIndex + packagesPerPageCount();
        setVisibleDefaultYatras(defaultYatras.slice(startIndex, endIndex));
        setDefaultPage(pageNumber);
        window.scrollTo({ top: 350, behavior: 'smooth' });
    };

    const packagesPerPageCount = () => yatrasPerPage;

    // Keep handleSearch for backwards compatibility / future reuse with filters
    // eslint-disable-next-line no-unused-vars
    const handleSearch = (city = selectedCity, sort = sortBy, maxDays = durationMax, q = nameQuery) => {
        setSelectedCity(city);
        setSortBy(sort);
        setDurationMax(maxDays);
        setNameQuery(q);
        setCitySearchInput(city);
        setAppliedSearch(city || q || '');
        fetchFilteredYatras({ city, sort, maxDays, q, searchKeyword: city || q || '' });
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const trimmed = searchQuery.trim();
        skipNextFetchRef.current = true;
        setShowSuggestions(false);
        setSuggestions([]);
        setAppliedSearch(trimmed);
        if (trimmed) {
            navigate(`/yatra-list?city=${encodeURIComponent(trimmed)}`);
            const currentParams = new URLSearchParams(location.search);
            if (currentParams.get('city') === trimmed) {
                fetchFilteredYatras({ city: trimmed, sort: sortBy, maxDays: durationMax, q: '', searchKeyword: trimmed });
            }
        } else {
            navigate('/yatra-list');
            fetchFilteredYatras({ city: '', sort: sortBy, maxDays: durationMax, q: '', searchKeyword: '' });
        }
    };

    const handleSuggestionClick = (suggestion) => {
        const name = suggestion.name || '';
        skipNextFetchRef.current = true;
        setShowSuggestions(false);
        setSuggestions([]);
        setSearchQuery(name);
        if (suggestion.type === 'city') {
            setAppliedSearch(name);
            navigate(`/yatra-list?city=${encodeURIComponent(name)}`);
            const currentParams = new URLSearchParams(location.search);
            if (currentParams.get('city') === name) {
                fetchFilteredYatras({ city: name, sort: sortBy, maxDays: durationMax, q: '', searchKeyword: name });
            }
        } else if (suggestion.type === 'yatra') {
            window.open(`/yatra-details/${generateYatraSlug(suggestion.yatra || suggestion)}`, '_blank', 'noopener,noreferrer');
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handlePlaceChanged = () => {
        const inst = fromAutoRef.current;
        if (!inst) return;
        const place = inst.getPlace();
        const city = getCityFromPlace(place);
        if (city) {
            setSelectedCity(city);
            setCitySearchInput(city);
        }
    };

    // Handle card click for details page
    const handleCardDetailsClick = (yatra) => {
        window.open(`/yatra-details/${generateYatraSlug(yatra)}`, '_blank', 'noopener,noreferrer');
    };

    // --- LEAD HANDLING ---
    const handleAction = (e, type, yatraData) => {
        e.preventDefault();
        e.stopPropagation();

        if (isSubmitting) return;

        setActiveEntity({ data: yatraData, type: 'yatra' });
        setPendingAction(type);

        if (!user) {
            setIsLoginPopupOpen(true);
            return;
        }

        processLead(type, user, { data: yatraData, type: 'yatra' });
    };

    const handleLoginSuccess = (loggedInUser) => {
        const session = getValidSession();
        const resolvedUser = session?.user?.user || session?.user || loggedInUser;
        setUser(resolvedUser);
        setIsLoginPopupOpen(false);

        // If review modal was requested, open it
        if (selectedYatra) {
            setShowReviewModal(true);
        } else if (pendingAction && activeEntity) {
            processLead(pendingAction, resolvedUser, activeEntity);
        }
    };

    const processLead = async (actionType, userData, entity) => {
        setIsSubmitting(true);
        const currentUser = userData || user;
        const { data } = entity;

        const payload = {
            name: currentUser?.name || "Guest",
            email: currentUser?.email || "",
            mobile: (currentUser?.phone || currentUser?.mobile || "").replace(/^(\+91|91)/, ""),
            type: 'yatra', // The backend specifically expects 'yatra' here
            category: 'tirth_yatra',
            type_id: data.id,
            message: `User clicked ${actionType} for ${data.yatra_name}`
        };

        let success = false;
        try {
            const res = await axios.post(`${API_BASE_URL}/enquiries/create`, payload);
            if (res.data.success) {
                success = true;
                setShowThanksModal(true);
                setTimeout(() => {
                    handleFinalRedirect(actionType, data);
                }, 2500);
            }
        } catch (err) {
            toast.error("Error processing request. Please try again.");
            console.error(err);
        } finally {
            setIsSubmitting(false);
            if (!success) {
                setPendingAction(null);
            }
        }
    };

    const handleFinalRedirect = (actionType, data) => {
        setShowThanksModal(false);
        const contactNumber = data.phone_number || data.whatsapp_number;

        if (actionType === "call" && contactNumber) {
            window.location.href = `tel:${contactNumber}`;
        } else if (actionType === "whatsapp" && contactNumber) {
            window.open(`https://wa.me/${contactNumber}`, "_blank");
        } else if (actionType === "book") {
            toast.info("Proceeding to booking...");
            window.open(`/yatra-details/${generateYatraSlug(data)}`, '_blank', 'noopener,noreferrer');
        }
        setPendingAction(null);
        setActiveEntity(null);
    };

    const handleReviewClick = (yatra) => {
        setSelectedYatra(yatra);
        if (!user) {
            setIsLoginPopupOpen(true);
            return;
        }
        setShowReviewModal(true);
    };

    // eslint-disable-next-line no-unused-vars
    const durationOptions = [
        { value: 'Any', label: 'Select Duration' },
        ...Array.from({ length: 15 }, (_, i) => ({
            value: (i + 1).toString(),
            label: `${i + 1} Days`
        }))
    ];

    // eslint-disable-next-line no-unused-vars
    const sortOptions = [
        { value: '', label: 'Sorted By Price' },
        { value: 'low', label: 'Low to High' },
        { value: 'high', label: 'High to Low' },
    ];

    // eslint-disable-next-line no-unused-vars
    const customSelectStyles = {
        control: (base, state) => {
            const hasActiveValue = !['', 'Any'].includes(state.getValue()?.[0]?.value);
            return ({
                ...base,
                border: 'none',
                borderBottom: `1px solid ${hasActiveValue ? '#32AEE4' : '#8c8c8c'}`,
                borderRadius: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                padding: '0',
                minHeight: 'auto',
                fontFamily: "'Poppins'",
                fontWeight: '500',
                fontSize: '15px',
                color: 'black',
                cursor: 'pointer',
            });
        },
        valueContainer: (base) => ({
            ...base,
            padding: '8px',
            paddingLeft: '0',
        }),
        singleValue: (base, state) => ({
            ...base,
            color: ['', 'Any'].includes(state.data?.value) ? '#6c757d' : '#111827',
            fontWeight: 500,
            margin: '0',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
            margin: '0',
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
            color: 'black',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: '#32AEE4',
            padding: '8px',
        }),
        menu: (base) => ({
            ...base,
            fontFamily: "'Poppins'",
            fontSize: '15px',
            fontWeight: '500',
            zIndex: 9999,
            marginTop: '2px',
            textAlign: 'left',
        }),
        // eslint-disable-next-line no-unused-vars
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#32AEE4' : state.isFocused ? '#e2f2ff' : 'white',
            color: state.isSelected ? 'white' : 'black',
            cursor: 'pointer',
            padding: '10px 12px',
            textAlign: 'left',
        })
    };

    return (
        <div>
            <SEO title={appliedSearch ? `Tirth Yatra in ${appliedSearch} | Mangoloops` : "Exclusive Tirth Yatra Packages | Mangoloops"} description="Discover sacred pilgrimage yatra packages with Mangoloops. From spiritual escapes to divine darshan tours, find your perfect yatra today." />
            <Header />
            <LoginPopup isOpen={isLoginPopupOpen} onClose={() => setIsLoginPopupOpen(false)} onLoginSuccess={handleLoginSuccess} />
            <main id="content">

                {/* ── SEARCH BAR (Copied from TirthYatra page) ── */}
                <div className="banner-content tour-search-sec text-center mb-5 mb-lg-8" style={{ backgroundImage: "url('/assets/img/pilgrimage/banner.webp')" }}>
                    <div className="container-fluid">
                        <div className="row justify-content-center py-xl-4">
                            <div className="col-lg-8">
                                <div className="card-body">
                                    <form className="form-container search-input-form" onSubmit={handleSearchSubmit}>
                                        <div className="search-container page-search-autocomplete" ref={searchContainerRef}>
                                            <span><i className="fa fa-search search-icon"></i></span>
                                            <input
                                                type="text"
                                                placeholder="Enter City or Yatra name"
                                                className="search-input"
                                                value={searchQuery}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    skipNextFetchRef.current = false;
                                                    setShowSuggestions(true);
                                                    setSearchQuery(val);
                                                    if (val === '') {
                                                        setSuggestions([]);
                                                        setShowSuggestions(false);
                                                        setAppliedSearch('');
                                                        setSelectedCity('');
                                                        setNameQuery('');
                                                        navigate('/yatra-list');
                                                        fetchFilteredYatras({ city: '', sort: sortBy, maxDays: durationMax, q: '', searchKeyword: '' });
                                                    }
                                                }}
                                                onFocus={() => {
                                                    if (searchQuery.trim().length >= 2 && suggestions.length > 0 && !skipNextFetchRef.current) {
                                                        setShowSuggestions(true);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') {
                                                        setShowSuggestions(false);
                                                    }
                                                }}
                                                autoComplete="off"
                                            />
                                            <button type="submit" className="search-button">Search</button>

                                            {showSuggestions && isSuggestionsLoading && (
                                                <div className="suggestions-dropdown"><div className="suggestion-item">Loading...</div></div>
                                            )}

                                            {showSuggestions && !isSuggestionsLoading && suggestions.length > 0 && (
                                                <div className="suggestions-dropdown">
                                                    {suggestions.map((item, index) => (
                                                        <div
                                                            key={`${item.type}-${item.id || index}`}
                                                            className="suggestion-item"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                handleSuggestionClick(item);
                                                            }}
                                                        >
                                                            <strong>{item.name}</strong>
                                                            <small>— {item.type === 'yatra' ? 'Tirth Yatra' : 'City'}{item.city ? ` · ${item.city}` : ''}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── COMMENTED PREVIOUS FILTERS (kept for future use) ──
                <div className="banner-content tour-search-sec text-center mb-5 holiday-listing-filter-section" style={{ backgroundImage: "url('/assets/img/pilgrimage/banner.webp')" }}>
                    <div className="container-fluid container-package">
                        <div className="row justify-content-center py-xl-4">
                            <div className="card border-0 border-radius-12 tab-shadow holiday-listing-filter-card">
                                <div className="card-body padding-14">
                                <form className="filter-form holiday-listing-filter-form p-0" onSubmit={(e) => { e.preventDefault(); handleSearch(citySearchInput || selectedCity || '', sortBy, durationMax, nameQuery); }}>
                                    <div className="form-group holiday-filter-field" style={{ minWidth: 200, position: 'relative', textAlign: 'left' }}>
                                        <div style={{ position: 'relative' }}>
                                            {isLoaded ? (
                                                <Autocomplete
                                                    onLoad={(inst) => {
                                                        fromAutoRef.current = inst;
                                                        try {
                                                            inst.setFields(["address_components", "name", "formatted_address"]);
                                                        } catch (e) { }
                                                    }}
                                                    onPlaceChanged={handlePlaceChanged}
                                                    options={{
                                                        types: ["(cities)"]
                                                    }}
                                                >
                                                    <input
                                                        type="text"
                                                        id="from-city"
                                                        className={`input-holiday ${citySearchInput ? 'filter-value-active' : ''}`}
                                                        placeholder="All Cities"
                                                        value={citySearchInput}
                                                        onChange={(e) => {
                                                            setCitySearchInput(e.target.value);
                                                            if (e.target.value === '') {
                                                                setSelectedCity('');
                                                            }
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            border: 'none',
                                                            borderBottom: '1px solid #8c8c8c',
                                                            fontFamily: 'Poppins',
                                                            fontSize: '15px',
                                                            fontWeight: '500'
                                                        }}
                                                    />
                                                </Autocomplete>
                                            ) : (
                                                <input
                                                    type="text"
                                                    id="from-city"
                                                    className={`input-holiday ${citySearchInput ? 'filter-value-active' : ''}`}
                                                    placeholder="All Cities"
                                                    value={citySearchInput}
                                                    onChange={(e) => {
                                                        setCitySearchInput(e.target.value);
                                                        if (e.target.value === '') {
                                                            setSelectedCity('');
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: 'none',
                                                        borderBottom: '1px solid #8c8c8c',
                                                        fontFamily: 'Poppins',
                                                        fontSize: '15px',
                                                        fontWeight: '500'
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="sort-duration-wrapper">
                                        <div className="form-group form-border-left holiday-filter-field" style={{ minWidth: 150, textAlign: 'left' }}>
                                            <Select
                                                inputId="sorted-by"
                                                name="sorted-by"
                                                value={sortOptions.find(o => o.value === sortBy) || sortOptions[0]}
                                                onChange={(selectedOption) => setSortBy(selectedOption ? selectedOption.value : '')}
                                                options={sortOptions}
                                                styles={customSelectStyles}
                                                isSearchable={false}
                                                maxMenuHeight={180}
                                            />
                                        </div>
                                        <div className="form-group form-border-left holiday-filter-field" style={{ minWidth: 150, textAlign: 'left' }}>
                                            <Select
                                                id="duration"
                                                name="duration"
                                                value={durationOptions.find(o => o.value.toString() === durationMax.toString()) || durationOptions[0]}
                                                onChange={(selectedOption) => setDurationMax(selectedOption ? selectedOption.value : 'Any')}
                                                options={durationOptions}
                                                styles={customSelectStyles}
                                                isSearchable={true}
                                                placeholder="Any"
                                                maxMenuHeight={220}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group form-border-left holiday-filter-field" style={{ minWidth: 220 }}>
                                        <input className={`input-holiday ${nameQuery ? 'filter-value-active' : ''}`}
                                            id="yatra-name"
                                            type="text"
                                            placeholder="Enter yatra name"
                                            value={nameQuery}
                                            onChange={(e) => setNameQuery(e.target.value)}
                                        />
                                    </div>
                                    <button type="submit" className="submit-btn-filter">Search</button>
                                </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                ── END OF COMMENTED PREVIOUS FILTERS ── */}

                <div className="mb-7">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h3 className="tour-title-of-trip holidays-left mb-0 heading-div">
                                    {appliedSearch ? `Tirth Yatra Packages for ${appliedSearch}` : 'Tirth Yatra Packages'}
                                </h3>
                                <div className="d-flex align-items-center flex-wrap gap-3">
                                    <ListingFilterBar
                                        priceSort={priceSort}
                                        onPriceSortChange={setPriceSort}
                                        isTopRated={isTopRated}
                                        onTopRatedToggle={() => setIsTopRated(prev => !prev)}
                                        isVerifiedOnly={isVerifiedOnly}
                                        onVerifiedToggle={() => setIsVerifiedOnly(prev => !prev)}
                                    />
                                    {!isLoading && (
                                        <div className="text-muted" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                                            Showing {visibleYatras.length} results of {filteredYatras.length}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="row mt-2">
                            {isLoading && (
                                <div className="col-12 text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                            {!isLoading && visibleYatras.length === 0 && (
                                <div className="col-12">
                                    <div className="alert alert-info text-center">
                                        No yatras found for the selected filters.
                                    </div>
                                </div>
                            )}
                            {!isLoading && visibleYatras.map((pkg) => (
                                <div className="col-12 mb-4" key={pkg.id}>
                                    <YatraCard
                                        yatra={pkg}
                                        onAction={handleAction}
                                        onReviewClick={handleReviewClick}
                                        onCardClick={handleCardDetailsClick}
                                        isSubmitting={isSubmitting}
                                        pendingAction={pendingAction}
                                        activeId={activeEntity?.data?.id}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* PAGINATION */}
                        {!isLoading && filteredYatras.length > yatrasPerPage && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(filteredYatras.length / yatrasPerPage)}
                                onPageChange={handlePageChange}
                            />
                        )}

                        {/* DEFAULT YATRAS SECTION WHEN SEARCH HAS 0 RESULTS */}
                        {!isLoading && visibleYatras.length === 0 && appliedSearch && defaultYatras.length > 0 && (
                            <div className="mt-5">
                                <div className="row">
                                    <div className="col-12 d-flex justify-content-between align-items-center mb-3 flex-wrap">
                                        <h3 className="tour-title-of-trip holidays-left mb-0 heading-div">Tirth Yatra Packages</h3>
                                        <div className="text-muted" style={{ fontSize: '14px' }}>
                                            Showing {visibleDefaultYatras.length} results of {defaultYatras.length}
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-2">
                                    {visibleDefaultYatras.map((pkg) => (
                                        <div className="col-12 mb-4" key={pkg.id}>
                                            <YatraCard
                                                yatra={pkg}
                                                onAction={handleAction}
                                                onReviewClick={handleReviewClick}
                                                onCardClick={handleCardDetailsClick}
                                                isSubmitting={isSubmitting}
                                                pendingAction={pendingAction}
                                                activeId={activeEntity?.data?.id}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* DEFAULT YATRAS PAGINATION */}
                                {defaultYatras.length > yatrasPerPage && (
                                    <Pagination
                                        currentPage={defaultPage}
                                        totalPages={Math.ceil(defaultYatras.length / yatrasPerPage)}
                                        onPageChange={handleDefaultPageChange}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />

            {/* --- THANK YOU MODAL --- */}
            {(isSubmitting || showThanksModal) && !isLoginPopupOpen && (
                <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
                    <div className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        {isSubmitting && !showThanksModal ? (
                            <>
                                <div className="mb-4">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                                        <span className="visually-hidden">Processing...</span>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Processing your request...</h4>
                                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
                            </>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <div className="success-checkmark-wrap">
                                        <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                                    </div>
                                </div>
                                <h2 className="fw-bold mb-3">Thank You!</h2>
                                <p className="text-muted mb-4">Your request has been sent to the vendor. We will get back to you shortly.</p>
                                <button className="btn btn-primary rounded-pill px-4 py-2 fw-bold" onClick={() => handleFinalRedirect(pendingAction, activeEntity.data)}>
                                    OK
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* --- REVIEW MODAL --- */}
            {showReviewModal && selectedYatra && (
                <ReviewModal
                    isOpen={showReviewModal}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedYatra(null);
                    }}
                    entity={selectedYatra}
                    entityType="yatra"
                    user={user}
                    apiEndpoint="/yatras"
                />
            )}
        </div>
    );
}

export default YatraListing;
